import React, { useState, useRef, useEffect } from 'react';
import './LanguageSwitcher.css';

export interface Language {
  code: string;
  label: string;
  flag?: string;
}

interface LanguageSwitcherProps {
  currentLanguage: string;
  languages: Language[];
  onLanguageChange: (language: string) => void;
  collapsed?: boolean;
  className?: string;
}

export const LanguageSwitcher: React.FC<LanguageSwitcherProps> = ({
  currentLanguage,
  languages,
  onLanguageChange,
  collapsed = false,
  className = ''
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const currentLang = languages.find(lang => lang.code === currentLanguage) || languages[0];

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLanguageSelect = (languageCode: string) => {
    onLanguageChange(languageCode);
    setIsOpen(false);
  };

  return (
    <div className={`language-switcher ${className}`} ref={dropdownRef}>
      <button
        className={`language-switcher__trigger ${collapsed ? 'collapsed' : ''}`}
        onClick={() => setIsOpen(!isOpen)}
        aria-label="Change language"
        title={collapsed ? `Language: ${currentLang.label}` : undefined}
      >
        <span className="language-switcher__current">
          {currentLang.flag && (
            <span className="language-switcher__flag">{currentLang.flag}</span>
          )}
          {!collapsed && (
            <span className="language-switcher__label">{currentLang.code.toUpperCase()}</span>
          )}
          {collapsed && (
            <span className="language-switcher__label--collapsed">{currentLang.code.toUpperCase().slice(0, 2)}</span>
          )}
        </span>
        {!collapsed && (
          <svg 
            className={`language-switcher__arrow ${isOpen ? 'open' : ''}`}
            width="12" 
            height="12" 
            viewBox="0 0 24 24" 
            fill="none"
          >
            <path 
              d="M6 9l6 6 6-6" 
              stroke="currentColor" 
              strokeWidth="2" 
              strokeLinecap="round" 
              strokeLinejoin="round"
            />
          </svg>
        )}
      </button>

      {isOpen && (
        <div className={`language-switcher__dropdown ${collapsed ? 'collapsed' : ''}`}>
          {languages.map((language) => (
            <button
              key={language.code}
              className={`language-switcher__option ${
                language.code === currentLanguage ? 'active' : ''
              }`}
              onClick={() => handleLanguageSelect(language.code)}
            >
              {language.flag && (
                <span className="language-switcher__flag">{language.flag}</span>
              )}
              <span className="language-switcher__option-label">
                {collapsed ? language.code.toUpperCase() : language.label}
              </span>
              {language.code === currentLanguage && (
                <svg 
                  className="language-switcher__check" 
                  width="16" 
                  height="16" 
                  viewBox="0 0 24 24" 
                  fill="none"
                >
                  <path 
                    d="M20 6L9 17l-5-5" 
                    stroke="currentColor" 
                    strokeWidth="2" 
                    strokeLinecap="round" 
                    strokeLinejoin="round"
                  />
                </svg>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};